import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import {
  InvoiceGroupCardSkeleton,
  LoadMoreButton,
  useReviewQueueNavigation,
} from "@/accounts-payable/common";
import { useResolveAgentAction } from "@/accounts-payable/query";
import { getBulkApproveIds } from "@/accounts-payable/utils";
import { InvoiceGroupCard } from "./invoice-group-card";

export const ApCombinedInvoicesTab = ({
  businessId,
  bulkApproving,
  onBulkApprove,
  onBrowseInvoices,
}: {
  businessId: string | null;
  bulkApproving: boolean;
  onBulkApprove: (ids: string[]) => void;
  /** Sends an empty queue somewhere useful instead of naming a tab in prose. */
  onBrowseInvoices?: () => void;
}) => {
  const {
    queue,
    items: queueItems,
    viewportRef,
    awaitingPosition,
    openInvoice,
    restoring,
  } = useReviewQueueNavigation(businessId);
  const {
    isPending,
    isError,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = queue;
  const resolve = useResolveAgentAction(businessId);

  const onResolve = (id: string, decision: "approved" | "rejected") =>
    resolve.mutate({ actionId: id, decision });

  return (
    <ScrollArea
      viewportRef={viewportRef}
      vertical
      type="scroll"
      className="h-full min-h-0 w-full"
    >
      {isPending || awaitingPosition ? (
        <Stack gap="md" fullWidth className="py-4">
          <InvoiceGroupCardSkeleton rows={3} />
          <InvoiceGroupCardSkeleton rows={3} />
          <InvoiceGroupCardSkeleton rows={2} />
        </Stack>
      ) : isError && (restoring || queueItems.length === 0) ? (
        <Stack gap="sm" align="center" fullWidth className="py-12">
          <span className="text-md text-tertiary-text">
            Couldn't load the invoices. Please try again.
          </span>
          <Button
            size="xsPill"
            variant="tertiary"
            loading={isFetching}
            onClick={() => void queue.refetch()}
          >
            Try again
          </Button>
        </Stack>
      ) : queueItems.length > 0 ? (
        <Stack gap="md" fullWidth className="py-4">
          {queueItems.map((item) => (
            <InvoiceGroupCard
              key={item.id}
              item={item}
              businessId={businessId}
              onResolve={onResolve}
              enableBulkApprove
              bulkApproving={bulkApproving}
              onOpenInvoice={openInvoice}
              onApproveAll={() =>
                onBulkApprove(
                  getBulkApproveIds(item.last_completed_agent_run.actions),
                )
              }
            />
          ))}
          {hasNextPage && (
            <Stack fullWidth className="pb-4">
              <LoadMoreButton
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
                disabled={isFetching}
              />
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack
          gap="sm"
          align="center"
          fullWidth
          className="mt-4 rounded-lg border border-stroke-tertiary bg-card py-12 text-center"
        >
          <span className="text-lg font-semibold">All caught up</span>
          <span className="text-md text-tertiary-text">
            The agent has nothing waiting on you right now.
          </span>
          {onBrowseInvoices && (
            <Button
              size="xsPill"
              variant="tertiary"
              className="mt-1"
              onClick={onBrowseInvoices}
            >
              Browse all invoices
            </Button>
          )}
        </Stack>
      )}
    </ScrollArea>
  );
};
